/**
 * ReACD CyberChatbot Widget - Styles
 * Modern, responsive chat widget with dark/light mode support
 */

export const WIDGET_STYLES = `
  * {
    box-sizing: border-box;
  }

  .reacd-chat-widget-container {
    position: fixed;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
    z-index: 999999;
    font-size: 14px;
    /* Default CSS variables */
    --chat-primary: #0d3d66;
    --chat-header-bg: #0d3d66;
    --chat-header-text: #ffffff;
    --chat-bg: #ffffff;
    --chat-text: #333333;
    --chat-user-msg-bg: #0d3d66;
    --chat-user-msg-text: #ffffff;
    --chat-assistant-msg-bg: #f5f5f5;
    --chat-assistant-msg-text: #333333;
    --chat-input-bg: #ffffff;
    --chat-input-text: #333333;
    --chat-input-border: #d0d0d0;
    --chat-send-btn-bg: #0d3d66;
    --chat-send-btn-text: #ffffff;
    --chat-btn-shadow: rgba(0, 0, 0, 0.15);
  }

  .reacd-chat-widget-container.bottom-right {
    bottom: 20px;
    right: 20px;
  }

  .reacd-chat-widget-container.bottom-left {
    bottom: 20px;
    left: 20px;
  }

  .reacd-chat-widget-container.top-right {
    top: 20px;
    right: 20px;
  }

  .reacd-chat-widget-container.top-left {
    top: 20px;
    left: 20px;
  }

  /* Floating Button */
  .reacd-chat-toggle-btn {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    position: relative;
    z-index: 1000;
    background: var(--chat-primary);
    color: white;
  }

  .reacd-chat-toggle-btn img {
    width: 32px;
    height: 32px;
    object-fit: contain;
    border-radius: 4px;
  }

  .reacd-chat-toggle-btn.bouncing {
    animation: reacd-chat-bounce 1.5s ease infinite;
  }

  @keyframes reacd-chat-bounce {
    0%, 100% { transform: translateY(0); }
    25% { transform: translateY(-8px); }
    50% { transform: translateY(0); }
    75% { transform: translateY(-4px); }
  }

  .reacd-chat-bubble {
    position: absolute;
    right: 70px;
    bottom: 10px;
    background: var(--chat-primary, #0d3d66);
    color: #fff;
    padding: 8px 14px;
    border-radius: 10px;
    font-size: 14px;
    white-space: nowrap;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    opacity: 0;
    transform: translateX(10px);
    transition: opacity 0.3s ease, transform 0.3s ease;
    pointer-events: none;
    z-index: 1001;
  }

  .reacd-chat-bubble::after {
    content: '';
    position: absolute;
    right: -6px;
    top: 50%;
    transform: translateY(-50%);
    border-width: 6px 0 6px 6px;
    border-style: solid;
    border-color: transparent transparent transparent var(--chat-primary, #0d3d66);
  }

  .reacd-chat-bubble.visible {
    opacity: 1;
    transform: translateX(0);
  }

  .reacd-chat-toggle-btn:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }

  .reacd-chat-toggle-btn:active {
    transform: scale(0.95);
  }

  /* Badge */
  .reacd-chat-badge {
    position: absolute;
    top: -5px;
    right: -5px;
    background: #ef4444;
    color: white;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
    border: 2px solid white;
  }

  /* Chat Window */
  .reacd-chat-window {
    position: fixed;
    display: flex;
    flex-direction: column;
    border-radius: 12px;
    box-shadow: 0 5px 40px rgba(0, 0, 0, 0.16);
    opacity: 0;
    transform: scale(0.95) translateY(10px);
    pointer-events: none;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 999;
    background: var(--chat-bg);
    color: var(--chat-text);
  }

  .reacd-chat-window.open {
    opacity: 1;
    transform: scale(1) translateY(0);
    pointer-events: auto;
  }

  .reacd-chat-window.resizing {
    transition: none !important;
    will-change: width, height;
  }

  /* Ensure input stays at bottom */
  .reacd-chat-window .reacd-chat-input {
    flex-shrink: 0;
  }

  .reacd-chat-window.bottom-right {
    bottom: 90px;
    right: 20px;
  }

  .reacd-chat-window.bottom-left {
    bottom: 90px;
    left: 20px;
  }

  .reacd-chat-window.top-right {
    top: 90px;
    right: 20px;
  }

  .reacd-chat-window.top-left {
    top: 90px;
    left: 20px;
  }

  /* Light theme */
  .reacd-chat-widget-container.light .reacd-chat-toggle-btn {
    background: var(--chat-primary);
    color: white;
  }

  .reacd-chat-widget-container.light .reacd-chat-header {
    background: var(--chat-header-bg);
    color: var(--chat-header-text);
    border-bottom: none;
  }

  .reacd-chat-widget-container.light .reacd-chat-message.user {
    background: var(--chat-user-msg-bg);
    color: var(--chat-user-msg-text);
  }

  .reacd-chat-widget-container.light .reacd-chat-message.assistant {
    background: var(--chat-assistant-msg-bg);
    color: var(--chat-assistant-msg-text);
  }

  .reacd-chat-widget-container.light .reacd-chat-input-bar {
    background: transparent;
    border-top: none;
  }

  .reacd-chat-widget-container.light .reacd-chat-input-inner {
    background: var(--chat-input-bg);
    border: 1px solid #e0e0e0;
    box-shadow: 0 4px 24px rgba(0,0,0,0.08);
  }

  .reacd-chat-widget-container.light .reacd-chat-input-field {
    background: transparent;
    color: var(--chat-input-text);
    border: none;
  }

  .reacd-chat-widget-container.light .reacd-chat-input-field:focus {
    outline: none;
  }

  /* Dark theme */
  .reacd-chat-widget-container.dark .reacd-chat-toggle-btn {
    background: var(--chat-primary);
    color: white;
  }

  .reacd-chat-widget-container.dark .reacd-chat-header {
    background: var(--chat-header-bg);
    color: var(--chat-header-text);
    border-bottom: 1px solid var(--chat-primary);
  }

  .reacd-chat-widget-container.dark .reacd-chat-message.user {
    background: var(--chat-user-msg-bg);
    color: var(--chat-user-msg-text);
  }

  .reacd-chat-widget-container.dark .reacd-chat-message.assistant {
    background: var(--chat-assistant-msg-bg);
    color: var(--chat-assistant-msg-text);
  }

  .reacd-chat-widget-container.dark .reacd-chat-input-bar {
    background: transparent;
    border-top: none;
  }

  .reacd-chat-widget-container.dark .reacd-chat-input-inner {
    background: #1e293b;
    border: 1px solid #334155;
    box-shadow: 0 4px 24px rgba(0,0,0,0.2);
  }

  .reacd-chat-widget-container.dark .reacd-chat-input-field {
    background: transparent;
    color: var(--chat-input-text);
    border: none;
  }

  .reacd-chat-widget-container.dark .reacd-chat-input-field:focus {
    outline: none;
  }

  /* Header */
  .reacd-chat-header {
    padding: 8px 16px;
    border-radius: 12px 12px 0 0;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: 12px;
  }

  .reacd-chat-header-logo {
    height: 36px;
    width: auto;
    object-fit: contain;
    flex-shrink: 0;
    display: block;
  }

  .reacd-chat-logo-wrapper {
    display: inline-flex;
    border-radius: 6px;
    overflow: hidden;
  }

  .reacd-chat-header-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .reacd-chat-header-title {
    font-size: 14px;
    font-weight: 600;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .reacd-chat-header-subtitle {
    font-size: 11px;
    opacity: 0.8;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .reacd-chat-close-btn {
    background: none;
    border: none;
    color: currentColor;
    font-size: 20px;
    cursor: pointer;
    padding: 4px 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.7;
    transition: opacity 0.2s;
    margin-left: auto;
  }

  .reacd-chat-close-btn:hover {
    opacity: 1;
  }

  /* Messages Container */
  .reacd-chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .reacd-chat-messages::-webkit-scrollbar {
    width: 6px;
  }

  .reacd-chat-widget-container.dark .reacd-chat-messages::-webkit-scrollbar-track {
    background: #0f3460;
  }

  .reacd-chat-widget-container.light .reacd-chat-messages::-webkit-scrollbar-track {
    background: #f5f5f5;
  }

  .reacd-chat-widget-container.dark .reacd-chat-messages::-webkit-scrollbar-thumb {
    background: #16213e;
    border-radius: 3px;
  }

  .reacd-chat-widget-container.light .reacd-chat-messages::-webkit-scrollbar-thumb {
    background: #d0d0d0;
    border-radius: 3px;
  }

  /* Message (modern speech bubbles) */
  .reacd-chat-message {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 8px;
    max-width: 85%;
    padding: 10px 14px;
    border-radius: 18px;
    word-wrap: break-word;
    white-space: normal;
    line-height: 1.5;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    animation: widget-message-appear 0.25s ease-out;
    position: relative;
    transition: box-shadow 0.2s ease;
  }

  @keyframes widget-message-appear {
    from {
      opacity: 0;
      transform: translateY(8px) scale(0.97);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  .reacd-chat-message.user {
    align-self: flex-end;
    border-radius: 18px 18px 4px 18px;
  }

  .reacd-chat-message.assistant {
    align-self: flex-start;
    border-radius: 18px 18px 18px 4px;
    padding-bottom: 6px;
  }

  .reacd-chat-message-content {
    font-size: 14px;
    line-height: 1.6;
    display: block;
    flex: 1;
    min-width: 0;
  }

  .reacd-chat-message-content p {
    margin: 0 0 3px 0;
  }

  .reacd-chat-message-content p:last-child {
    margin-bottom: 0;
  }

  .reacd-chat-message-content code {
    background: rgba(0, 0, 0, 0.1);
    padding: 2px 6px;
    border-radius: 3px;
    font-family: 'Monaco', 'Courier New', monospace;
    font-size: 12px;
  }

  .reacd-chat-message-content pre {
    background: rgba(0, 0, 0, 0.2);
    padding: 10px;
    border-radius: 6px;
    overflow-x: auto;
    margin: 6px 0;
  }

  .reacd-chat-message-content pre code {
    background: none;
    padding: 0;
    color: inherit;
  }

  .reacd-chat-message-content ul,
  .reacd-chat-message-content ol {
    margin: 6px 0;
    padding-left: 20px;
  }

  .reacd-chat-message-content li {
    margin: 2px 0;
  }

  /* Streaming indicator */
  .reacd-chat-message.streaming {
    opacity: 0.8;
  }

  .reacd-chat-message-content.streaming::after {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
    animation: blink 1.4s infinite;
    margin-left: 6px;
  }

  @keyframes blink {
    0%, 60%, 100% {
      opacity: 0.3;
    }
    30% {
      opacity: 1;
    }
  }

  /* Input Area (floating bar like dashboard) */
  .reacd-chat-input-bar {
    padding: 0 12px 12px;
    border-top: none;
    background: transparent;
    flex-shrink: 0;
  }

  .reacd-chat-input-inner {
    display: flex;
    gap: 8px;
    align-items: flex-end;
    background: var(--chat-input-bg);
    border: 1px solid var(--chat-input-border);
    border-radius: 20px;
    padding: 6px 10px;
    box-shadow: 0 4px 24px rgba(0,0,0,0.08);
    max-width: 100%;
    margin: 0 auto;
    transition: max-width 0.4s ease, padding 0.4s ease;
  }

  .reacd-chat-input-inner.phone-mode {
    width: fit-content;
    max-width: 100%;
    justify-content: center;
    gap: 0;
    align-items: center;
    flex-wrap: nowrap;
    overflow: hidden;
  }

  .reacd-chat-input-field,
  .reacd-chat-send-btn,
  .reacd-chat-stop-btn,
  .reacd-chat-mic-btn,
  .reacd-chat-emotion-btn {
    transition: all 0.4s ease;
  }

  .reacd-chat-input-field.phone-mode-hidden {
    display: none !important;
    opacity: 0;
    flex: 0;
    width: 0;
    height: 0;
    min-height: 0;
    padding: 0;
    margin: 0;
    border: none;
    overflow: hidden;
    pointer-events: none;
  }

  .reacd-chat-send-btn.phone-mode-hidden,
  .reacd-chat-stop-btn.phone-mode-hidden,
  .reacd-chat-mic-btn.phone-mode-hidden,
  .reacd-chat-emotion-btn.phone-mode-hidden {
    opacity: 0;
    width: 0;
    height: 0;
    padding: 0;
    margin: 0;
    transform: scale(0);
    overflow: hidden;
    pointer-events: none;
  }

  .reacd-chat-input-field {
    flex: 1;
    padding: 6px 10px;
    border: none;
    border-radius: 16px;
    font-size: 14px;
    font-family: inherit;
    background: transparent;
    color: var(--chat-input-text);
    resize: none;
    max-height: 100px;
    min-height: 32px;
    overflow-y: hidden;
    line-height: 1.5;
  }

  .reacd-chat-input-field:focus {
    outline: none;
  }

  .reacd-chat-input-field::placeholder {
    opacity: 0.5;
  }

  .reacd-chat-input-field:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .reacd-chat-send-btn {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--chat-send-btn-bg);
    color: var(--chat-send-btn-text);
    border: none;
    border-radius: 50%;
    cursor: pointer;
    flex-shrink: 0;
    transition: filter 0.15s ease, transform 0.1s ease;
  }

  .reacd-chat-send-btn:hover:not(:disabled) {
    filter: brightness(1.1);
  }

  .reacd-chat-send-btn:active:not(:disabled) {
    transform: scale(0.96);
  }

  .reacd-chat-send-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Stop button */
  .reacd-chat-stop-btn {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    background: transparent;
    border: none;
    cursor: pointer;
    flex-shrink: 0;
  }

  .reacd-chat-stop-pulse {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #dc2626;
    animation: widget-stop-pulse 1.6s ease-in-out infinite;
  }

  .reacd-chat-stop-btn:hover .reacd-chat-stop-pulse {
    animation-play-state: paused;
    opacity: 0.9;
  }

  .reacd-chat-stop-icon {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
  }

  @keyframes widget-stop-pulse {
    0%, 100% { transform: scale(1); opacity: 0.55; }
    50% { transform: scale(1.2); opacity: 0.85; }
  }

  /* Empty state */
  .reacd-chat-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
    opacity: 0.5;
    text-align: center;
    padding: 20px;
  }

  .reacd-chat-empty-state-icon {
    font-size: 40px;
    margin-bottom: 12px;
  }

  .reacd-chat-empty-state-text {
    font-size: 14px;
    line-height: 1.45;
  }

  /* Error state */
  .reacd-chat-error {
    background: #fee2e2;
    border: 1px solid #fca5a5;
    color: #dc2626;
    padding: 12px 16px;
    border-radius: 8px;
    margin: 0;
  }

  .reacd-chat-widget-container.dark .reacd-chat-error {
    background: #7f1d1d;
    border-color: #dc2626;
    color: #fca5a5;
  }

  .reacd-chat-error-message {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fffbeb;
    border: 1px solid #fbbf24;
    color: #92400e;
    padding: 12px 16px;
    border-radius: 8px;
    margin: 8px 16px;
    font-size: 13px;
    font-weight: 500;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  }

  .reacd-chat-widget-container.dark .reacd-chat-error-message {
    background: #451a03;
    border-color: #f59e0b;
    color: #fcd34d;
  }

  .reacd-chat-error-icon {
    flex-shrink: 0;
    font-size: 16px;
    line-height: 1;
  }

  /* Responsive */
  @media (max-width: 600px) {
    .reacd-chat-window {
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      bottom: 0 !important;
      right: 0 !important;
      width: 100vw !important;
      height: 100vh !important;
      border-radius: 0 !important;
      max-width: none !important;
      max-height: none !important;
    }

    .reacd-chat-window.open {
      transform: none !important;
    }

    .reacd-chat-toggle-btn {
      display: none;
    }

    .reacd-chat-message {
      max-width: 90%;
    }
  }

  /* Loading spinner */
  .reacd-chat-spinner {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
    animation: spin 1.4s infinite;
  }

  @keyframes spin {
    0% { opacity: 0.3; }
    50% { opacity: 1; }
    100% { opacity: 0.3; }
  }

  /* Thinking animation - wave dots */
  .reacd-chat-thinking {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    color: var(--chat-text-secondary, #555);
    padding: 4px 0;
    transition: opacity 0.3s ease-out;
  }

  .reacd-chat-thinking.fade-out {
    opacity: 0;
    pointer-events: none;
  }

  .reacd-chat-thinking-label {
    font-size: 13px;
    opacity: 1;
  }

  .reacd-chat-thinking-dots {
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }

  .reacd-chat-thinking-dots span {
    display: inline-block;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.8;
    animation: thinking-wave 1.6s infinite ease-in-out both;
  }

  .reacd-chat-thinking-dots span:nth-child(1) {
    animation-delay: 0s;
  }

  .reacd-chat-thinking-dots span:nth-child(2) {
    animation-delay: 0.2s;
  }

  .reacd-chat-thinking-dots span:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes thinking-wave {
    0%, 80%, 100% {
      transform: scale(0.6);
      opacity: 0.5;
    }
    40% {
      transform: scale(1.2);
      opacity: 1;
    }
  }

  /* Privacy Modal - inside chat window */
  .reacd-chat-privacy-modal {
    display: none;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(13, 61, 102, 0.95);
    /* dynamic overlay color via inline style if needed */
    z-index: 10;
    align-items: center;
    justify-content: center;
    padding: 20px;
    border-radius: 12px;
  }

  .reacd-chat-privacy-content {
    background: white;
    border-radius: 12px;
    padding: 24px;
    max-width: 360px;
    width: 100%;
    box-shadow: 0 10px 40px rgba(13, 61, 102, 0.2);
  }

  .reacd-chat-privacy-title {
    margin: 0 0 16px 0;
    font-size: 18px;
    font-weight: 600;
    color: var(--chat-primary);
  }

  .reacd-chat-privacy-text {
    margin: 0 0 24px 0;
    font-size: 14px;
    line-height: 1.5;
    color: #475569;
  }

  .reacd-chat-privacy-buttons {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
  }

  .reacd-chat-privacy-accept,
  .reacd-chat-privacy-decline {
    padding: 10px 20px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    border: none;
    transition: all 0.2s;
  }

  .reacd-chat-privacy-accept {
    background: var(--chat-primary);
    color: white;
  }

  .reacd-chat-privacy-accept:hover {
    opacity: 0.9;
  }

  .reacd-chat-privacy-decline {
    background: #e2e8f0;
    color: #475569;
  }

  .reacd-chat-privacy-decline:hover {
    background: #cbd5e1;
  }

  /* HTML render toggle inside code blocks */
  .reacd-chat-code-toggle {
    display: inline-flex;
    gap: 0;
    background: rgba(0, 0, 0, 0.05);
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 6px;
    overflow: hidden;
    margin-bottom: 6px;
  }

  .reacd-chat-widget-container.dark .reacd-chat-code-toggle {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
  }

  .reacd-chat-code-toggle button {
    padding: 4px 10px;
    border: none;
    background: transparent;
    color: var(--chat-text-secondary, #888);
    font-size: 12px;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
  }

  .reacd-chat-code-toggle button.active {
    background: var(--chat-primary);
    color: white;
  }

  .reacd-chat-code-toggle button:not(.active):hover {
    background: rgba(0, 0, 0, 0.08);
  }

  .reacd-chat-widget-container.dark .reacd-chat-code-toggle button:not(.active):hover {
    background: rgba(255, 255, 255, 0.08);
  }

  .reacd-chat-html-iframe {
    width: 100%;
    min-height: 200px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 8px;
    background: white;
  }

  .reacd-chat-widget-container.dark .reacd-chat-html-iframe {
    border-color: rgba(255, 255, 255, 0.15);
  }

  /* Resize Handle */
  .reacd-chat-resize-handle {
    position: absolute;
    top: 0;
    left: 0;
    width: 30px;
    height: 30px;
    cursor: nwse-resize;
    background: rgba(13, 61, 102, 0.1);
    border-radius: 12px 0 0 0;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .reacd-chat-resize-handle::before {
    content: '⇲';
    font-size: 16px;
    color: var(--chat-primary);
    opacity: 0.7;
    transform: rotate(45deg);
  }

  /* Audio buttons: mic, phone */
  .reacd-chat-mic-btn,
  .reacd-chat-phone-btn {
    background: transparent;
    border: none;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--chat-text-secondary, #888);
    transition: background 0.2s ease, color 0.2s ease, transform 0.15s ease;
    position: relative;
    flex-shrink: 0;
  }

  .reacd-chat-mic-btn:hover,
  .reacd-chat-phone-btn:hover {
    background: var(--chat-primary);
    color: white;
    transform: scale(1.08);
  }

  .reacd-chat-mic-btn:active,
  .reacd-chat-phone-btn:active {
    transform: scale(0.95);
  }

  .reacd-chat-mic-btn.recording {
    background: #dc2626;
    color: white;
    animation: widget-mic-recording-pulse 1.5s ease-in-out infinite;
  }

  .reacd-chat-mic-btn.awaiting {
    animation: widget-mic-awaiting 1.5s ease-in-out infinite;
  }

  @keyframes widget-mic-awaiting {
    0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4); }
    50% { box-shadow: 0 0 0 6px rgba(220, 38, 38, 0); }
  }

  @keyframes widget-mic-recording-pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4); }
    50% { box-shadow: 0 0 0 6px rgba(220, 38, 38, 0); }
  }

  .reacd-chat-phone-btn.active {
    background: #dc2626;
    color: white;
    animation: widget-phone-pulse 2s ease-in-out infinite;
  }

  @keyframes widget-phone-pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4); }
    50% { box-shadow: 0 0 0 8px rgba(220, 38, 38, 0); }
  }

  /* Message actions (speaker button etc.) – outside bubble */
  .reacd-chat-message-actions {
    position: absolute;
    right: -32px;
    top: 4px;
    display: flex;
    gap: 6px;
    opacity: 0;
    transition: opacity 0.2s ease;
    flex-shrink: 0;
  }

  .reacd-chat-message:hover .reacd-chat-message-actions {
    opacity: 1;
  }

  /* Speaker button */
  .reacd-chat-speak-btn {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    color: var(--chat-text-secondary, #888);
    transition: background 0.2s ease, color 0.2s ease, transform 0.15s ease;
    position: relative;
  }

  .reacd-chat-speak-btn:hover {
    background: var(--chat-primary);
    color: white;
    transform: scale(1.1);
  }

  .reacd-chat-speak-btn:active {
    transform: scale(0.95);
  }

  .reacd-chat-speak-btn.speaking {
    background: #dc2626;
    color: white;
    opacity: 1;
    animation: widget-speak-pulse-red 1.2s ease-in-out infinite;
  }

  .reacd-chat-speak-btn .speak-ring {
    position: absolute;
    border-radius: 50%;
    border: 2px solid var(--chat-primary);
    opacity: 0;
    animation: widget-speak-pulse 1.4s ease-out infinite;
  }

  .reacd-chat-speak-btn.speaking .speak-ring {
    border-color: #dc2626;
  }

  .reacd-chat-speak-btn.speaking .speak-ring:nth-child(1) { animation-delay: 0s; }
  .reacd-chat-speak-btn.speaking .speak-ring:nth-child(2) { animation-delay: 0.35s; }
  .reacd-chat-speak-btn.speaking .speak-ring:nth-child(3) { animation-delay: 0.7s; }

  @keyframes widget-speak-pulse-red {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
  }

  @keyframes widget-speak-pulse {
    0% { width: 28px; height: 28px; opacity: 0.6; }
    100% { width: 48px; height: 48px; opacity: 0; }
  }

  .reacd-chat-speaking {
    animation: widget-speaking-glow 1s ease-in-out infinite alternate;
  }

  @keyframes widget-speaking-glow {
    from { box-shadow: 0 0 0 0 rgba(13, 61, 102, 0.2); }
    to { box-shadow: 0 0 0 6px rgba(13, 61, 102, 0.05); }
  }

  /* Reasoning Box */
  .reacd-chat-reasoning-box {
    margin-bottom: 8px;
    border-radius: 8px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    overflow: hidden;
  }
  .reacd-chat-reasoning-toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    padding: 8px 12px;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 12px;
    color: #475569;
  }
  .reacd-chat-reasoning-label {
    font-weight: 600;
  }
  .reacd-chat-reasoning-thinking-label {
    font-weight: 600;
  }
  .reacd-chat-reasoning-thinking-label::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #3b82f6;
    margin-right: 6px;
    animation: widget-reasoning-dot 1.4s infinite;
  }
  @keyframes widget-reasoning-dot {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
  .reacd-chat-reasoning-body {
    padding: 8px 12px;
    font-size: 13px;
    color: #334155;
    border-top: 1px solid #e2e8f0;
  }

  /* Tool Calls */
  .reacd-chat-toolcalls-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 8px;
  }
  .reacd-chat-toolcall-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    user-select: none;
  }
  .reacd-chat-toolcall-badge.ok {
    background: #dcfce7;
    color: #166534;
  }
  .reacd-chat-toolcall-badge.err {
    background: #fee2e2;
    color: #991b1b;
  }

  /* Terminal Box */
  .reacd-chat-terminal-box {
    margin-top: 8px;
    border-radius: 8px;
    background: #0f172a;
    border: 1px solid #1e293b;
    overflow: hidden;
  }
  .reacd-chat-terminal-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 8px 12px;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 12px;
    color: #94a3b8;
  }
  .reacd-chat-terminal-label {
    font-weight: 600;
  }
  .reacd-chat-terminal-body {
    padding: 8px 12px;
    font-size: 12px;
    color: #e2e8f0;
    border-top: 1px solid #1e293b;
    max-height: 200px;
    overflow-y: auto;
  }
  .reacd-chat-terminal-prompt {
    color: #22c55e;
    margin-bottom: 4px;
  }
  .reacd-chat-terminal-prompt-char {
    color: #22c55e;
    margin-right: 4px;
  }
  .reacd-chat-terminal-code {
    background: #1e293b;
    padding: 8px;
    border-radius: 4px;
    margin: 4px 0;
    overflow-x: auto;
    color: #e2e8f0;
  }
  .reacd-chat-terminal-output {
    color: #cbd5e1;
    margin: 4px 0;
    white-space: pre-wrap;
  }

  /* Modal */
  .reacd-chat-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000000;
    padding: 16px;
  }
  .reacd-chat-modal-box {
    background: var(--chat-bg, #fff);
    border-radius: 12px;
    max-width: 640px;
    width: 90vw;
    max-height: 80vh;
    overflow-y: auto;
    padding: 20px;
    box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);
  }
  .reacd-chat-modal-box.preview-modal {
    max-width: 90vw;
    width: 90vw;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    padding: 0;
  }
  .reacd-chat-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding: 16px 20px 0;
  }
  .reacd-chat-modal-header h4 {
    margin: 0;
    font-size: 16px;
  }
  .reacd-chat-modal-close {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 18px;
    color: #64748b;
    padding: 4px;
    line-height: 1;
  }
  .reacd-chat-modal-label {
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    margin-bottom: 4px;
    margin-top: 12px;
  }
  .reacd-chat-modal-args {
    background: #f1f5f9;
    padding: 10px;
    border-radius: 8px;
    font-size: 12px;
    overflow-x: auto;
    margin: 0;
  }
  .reacd-chat-modal-meta {
    font-size: 12px;
    color: #64748b;
    margin-bottom: 8px;
  }
  .reacd-chat-modal-err {
    font-size: 12px;
    color: #991b1b;
    background: #fee2e2;
    padding: 8px;
    border-radius: 6px;
    margin-bottom: 8px;
  }
  .reacd-chat-preview-content {
    flex: 1;
    padding: 0 20px 20px;
    min-height: 0;
  }
  .reacd-chat-preview-frame {
    width: 100%;
    height: 100%;
    border: none;
    border-radius: 8px;
    background: #fff;
  }

  /* HTML maximize button */
  .reacd-chat-html-iframe-wrapper {
    position: relative;
    max-width: 100%;
  }
  .reacd-chat-html-maximize {
    position: absolute;
    top: 4px;
    right: 4px;
    background: rgba(255,255,255,0.9);
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
  }

  /* Emotion button */
  .reacd-chat-emotion-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: var(--chat-text-secondary, #666);
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }
  .reacd-chat-emotion-btn:hover {
    background: rgba(0,0,0,0.05);
  }
  .reacd-chat-emotion-btn.active {
    color: #0d3d66;
  }
  .reacd-chat-emotion-btn.active::after {
    content: '';
    position: absolute;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #22c55e;
    bottom: 4px;
    right: 4px;
  }
  .reacd-chat-emotion-btn.error {
    color: #dc2626;
  }
`;

